import React, { useContext, forwardRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import CardListContext from './ListContext';
import styles from './Card.module.scss';

const Card = forwardRef(({
  title,
  titleComponent,
  actions,
  children,
  className,
  onClick,
  linkTo,
  noBorder,
  spacingBottom,
  withShadow,
  ...rest
}, ref) => {
  const TitleComponent = titleComponent || Fragment;
  const navigate = useNavigate();
  const { display: parentDisplay, columns: parentColumns } = useContext(CardListContext);

  const handleOnClick = (event) => {
    if (!(onClick || linkTo)) {
      return;
    }

    if (linkTo) {
      navigate(linkTo);
      return;
    }

    onClick(event);
  };

  const spacingBottomClass = spacingBottom ? `spacingBottom${spacingBottom.charAt(0).toUpperCase()}${spacingBottom.substring(1)}` : null;

  return (
    <div
      ref={ref}
      className={cn(
        styles.card,
        styles[parentDisplay],
        styles[`m${parentColumns}cols`],
        {
          [styles.active]: !!onClick || !!linkTo,
          [styles.noBorder]: noBorder,
          [styles.withShadow]: withShadow,
        },
        spacingBottomClass ? styles[spacingBottomClass] : null,
        className
      )}
      onClick={handleOnClick}
      {...rest}
    >
      {(title || actions.length > 0) && (
        <div className={cn(styles.header, { [styles.withActions]: actions.length > 0 })}>
          <div className={styles.title}>
            <TitleComponent>{title}</TitleComponent>
          </div>
          {actions.length > 0 && (
            <div
              className={styles.actions}
              onClick={e => e.stopPropagation()}
            >
              {actions}
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
});

Card.displayName = 'forwardRef(Card)';

Card.propTypes = {
  title: PropTypes.node,
  titleComponent: PropTypes.string,
  actions: PropTypes.arrayOf(PropTypes.node),
  className: PropTypes.string,
  onClick: PropTypes.func,
  linkTo: PropTypes.string,
  noBorder: PropTypes.bool,
  spacingBottom: PropTypes.oneOf(['small', 'medium', 'large']),
  withShadow: PropTypes.bool,
};

Card.defaultProps = {
  title: null,
  titleComponent: null,
  actions: [],
  className: '',
  onClick: null,
  linkTo: null,
  noBorder: false,
  spacingBottom: null,
  withShadow: false,
};

export default Card;
